package diana.soleil.design.iraniancanadienbazzar.core.data.fake

import diana.soleil.design.iraniancanadienbazzar.core.data.UserProfile
import diana.soleil.design.iraniancanadienbazzar.core.model.Address
import diana.soleil.design.iraniancanadienbazzar.core.model.AppNotification
import diana.soleil.design.iraniancanadienbazzar.core.model.BlockedUser
import diana.soleil.design.iraniancanadienbazzar.core.model.Business
import diana.soleil.design.iraniancanadienbazzar.core.model.BusinessClaim
import diana.soleil.design.iraniancanadienbazzar.core.model.BusinessStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.ClaimRole
import diana.soleil.design.iraniancanadienbazzar.core.model.ClaimStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.Comment
import diana.soleil.design.iraniancanadienbazzar.core.model.Conversation
import diana.soleil.design.iraniancanadienbazzar.core.model.DailyListingCounters
import diana.soleil.design.iraniancanadienbazzar.core.model.Event
import diana.soleil.design.iraniancanadienbazzar.core.model.EventCategory
import diana.soleil.design.iraniancanadienbazzar.core.model.EventStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.EventVenue
import diana.soleil.design.iraniancanadienbazzar.core.model.ItemCondition
import diana.soleil.design.iraniancanadienbazzar.core.model.ItemStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.ListingCounters
import diana.soleil.design.iraniancanadienbazzar.core.model.LocalizedText
import diana.soleil.design.iraniancanadienbazzar.core.model.MapPrecision
import diana.soleil.design.iraniancanadienbazzar.core.model.MarketplaceItem
import diana.soleil.design.iraniancanadienbazzar.core.model.PEOPLE_MAP_CHECK_IN_MILLIS
import diana.soleil.design.iraniancanadienbazzar.core.model.PublicProfile
import diana.soleil.design.iraniancanadienbazzar.core.model.MediaStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.Message
import diana.soleil.design.iraniancanadienbazzar.core.model.NewsArticle
import diana.soleil.design.iraniancanadienbazzar.core.model.NotificationType
import diana.soleil.design.iraniancanadienbazzar.core.model.Order
import diana.soleil.design.iraniancanadienbazzar.core.model.OrderItem
import diana.soleil.design.iraniancanadienbazzar.core.model.OrderStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.PaymentStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.Post
import diana.soleil.design.iraniancanadienbazzar.core.model.PostType
import diana.soleil.design.iraniancanadienbazzar.core.model.Product
import diana.soleil.design.iraniancanadienbazzar.core.model.PropertyDetails
import diana.soleil.design.iraniancanadienbazzar.core.model.Report
import diana.soleil.design.iraniancanadienbazzar.core.model.ReportStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.Review
import diana.soleil.design.iraniancanadienbazzar.core.model.Sponsorship
import diana.soleil.design.iraniancanadienbazzar.core.model.Ticket
import diana.soleil.design.iraniancanadienbazzar.core.model.TicketStatus
import diana.soleil.design.iraniancanadienbazzar.core.model.TicketType
import diana.soleil.design.iraniancanadienbazzar.core.model.UserAccount
import kotlin.time.Clock
import kotlin.time.Instant
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.number
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime

/**
 * The full QA scenario dataset for **mock-data mode** (Developer Menu → "Populate mock data").
 * Everything is in-memory and rebuilt fresh each time the mode is entered — nothing here ever
 * reaches Firestore. One coherent world, so cross-screen flows line up: the unread message
 * notification opens the seeded conversation, the order notification matches a seeded order, the
 * owned business has products/orders/analytics for the dashboard, the pending business/claim/
 * report/event fill the admin queues, and so on.
 *
 * The "signed-in" QA persona is [MOCK_UID] ("Mock Tester") — fakes resolve `isMine`/ownership
 * against it regardless of real auth, so ownership affordances are visible even signed out.
 * Timestamps are offsets from launch time so relative labels ("2 h ago", upcoming events) always
 * look alive. Domain values only; not localizable UI copy. Never shipped in a release path.
 */
internal object MockScenarios {

    const val MOCK_UID = "mock-user-me"
    const val MOCK_NAME = "Mock Tester"

    private val now = Clock.System.now().toEpochMilliseconds()
    private fun minutesAgo(m: Int) = now - m * 60_000L
    private fun hoursAgo(h: Int) = minutesAgo(h * 60)
    private fun daysAgo(d: Int) = hoursAgo(d * 24)
    private fun daysAhead(d: Int) = now + d * 24 * 3_600_000L

    /**
     * [d] days from now, at [hour] local time. Events inherited the launch clock before this, so a
     * concert or a poetry night could read "7:37 AM" — implausible on screen and in a store
     * screenshot. Demo events now start in the evening like the real ones do.
     */
    private fun daysAheadAt(d: Int, hour: Int = 19): Long {
        val zone = TimeZone.currentSystemDefault()
        val date = Instant.fromEpochMilliseconds(daysAhead(d)).toLocalDateTime(zone).date
        return LocalDateTime(date.year, date.month, date.day, hour, 0).toInstant(zone).toEpochMilliseconds()
    }

    private val everyDayHours = mapOf(
        "mon" to "11:00-22:00", "tue" to "11:00-22:00", "wed" to "11:00-22:00",
        "thu" to "11:00-23:00", "fri" to "11:00-23:00", "sat" to "12:00-23:00", "sun" to "12:00-21:00",
    )

    // ── Businesses ──────────────────────────────────────────────────────────
    // FakeData's approved directory, upgraded so every card/detail affordance has a case: open-hours
    // chip, chat + ordering CTAs, an owned business (dashboard/orders/products), and a realtor with
    // property listings. Plus one PENDING submission that only the admin queue sees.

    /** Owned by the QA persona — powers Dashboard, Manage products/reviews, order flow. */
    val ownedBusiness: Business = FakeData.businesses
        .first { it.id == "fake-tehran-grill" }
        .copy(
            ownerUid = MOCK_UID,
            claimStatus = ClaimStatus.Claimed,
            offersChat = true,
            offersOrdering = true,
            hours = everyDayHours,
        )

    /**
     * The full-featured real-estate showcase, ALSO owned by the QA persona: sponsored (Featured
     * carousel), verified, chat, hours, owner media, property listings incl. a promoted one
     * (Promotions rail), reviews with an owner reply, a live customer lead in the inbox, and
     * dashboard analytics + QR — every business feature exercisable from one listing.
     */
    val realtorBusiness: Business = Business(
        id = "mock-maple-homes",
        name = "Maple & Saffron Realty",
        serviceTitle = LocalizedText(en = "Real Estate Agent", fr = "Courtier immobilier", fa = "مشاور املاک"),
        ownerUid = MOCK_UID,
        claimStatus = ClaimStatus.Claimed,
        categoryId = "real_estate",
        description = LocalizedText(
            en = "Persian-speaking realtor for buying, selling, and renting across the GTA. Free evaluations, staging advice, and newcomer-friendly financing guidance.",
            fa = "مشاور املاک فارسی‌زبان برای خرید، فروش و اجاره در سراسر تورنتوی بزرگ.",
        ),
        status = BusinessStatus.Approved,
        verified = true,
        phone = "+14165550188",
        email = "hello@maplesaffron.example",
        website = "https://maplesaffron.example",
        address = Address(line = "88 Yonge St", city = "Toronto", province = "ON"),
        metroId = "gta",
        hours = everyDayHours,
        logoUrl = "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=200&q=60",
        coverUrl = "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1000&q=70",
        photos = listOf(
            "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1000&q=70",
            "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=1000&q=70",
            "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1000&q=70",
        ),
        mediaStatus = MediaStatus.OwnerMedia,
        languagesSpoken = listOf("Farsi", "English", "French"),
        offersChat = true,
        attributes = listOf("virtual_tours", "free_evaluation", "rentals", "newcomer_financing_help", "online_appointments"),
        priceLevel = 3,
        rating = 4.9,
        reviewCount = 21,
        sponsorship = Sponsorship(active = true, plan = "sponsored"),
    )

    val businesses: List<Business> = FakeData.businesses.map { business ->
        when (business.id) {
            ownedBusiness.id -> ownedBusiness
            // The flagship demo listing: chat + hours so the detail screen shows everything.
            "fake-shiraz-palace" -> business.copy(
                offersChat = true, hours = everyDayHours, ownerUid = "mock-owner-shiraz",
                attributes = listOf("halal", "dine_in", "takeout", "reservations", "outdoor_seating", "card_payments", "wheelchair_accessible"),
            )
            else -> business
        }
    } + realtorBusiness

    /** Awaiting moderation — visible only in the Admin approval queue. */
    val pendingBusiness: Business = Business(
        id = "mock-pending-cafe",
        name = "Café Golestan",
        serviceTitle = LocalizedText(en = "Persian Café"),
        categoryId = "food_restaurants",
        description = LocalizedText(en = "Saffron lattes, faloodeh, and board games. Submitted and awaiting review."),
        status = BusinessStatus.Pending,
        address = Address(line = "12 Rue Saint-Denis", city = "Montréal", province = "QC"),
        metroId = "mtl",
    )

    // ── Reviews ─────────────────────────────────────────────────────────────
    val reviews: List<Review> = listOf(
        Review(
            id = "mock-review-1", businessId = "fake-shiraz-palace", authorUid = "mock-user-neda",
            authorName = "Neda T.", rating = 5,
            comment = "The koobideh is the best I've had in Canada — saffron rice was perfect. Ask for the table by the window.",
            tags = listOf("Great food", "Friendly staff"),
            ownerReply = "Merci Neda jan! We'll keep the window table warm for you.",
            createdAtMillis = daysAgo(3),
        ),
        Review(
            id = "mock-review-2", businessId = "fake-shiraz-palace", authorUid = MOCK_UID,
            authorName = MOCK_NAME, rating = 4,
            comment = "Great ghormeh sabzi, slightly long wait on a Friday night.",
            createdAtMillis = daysAgo(10),
        ),
        Review(
            id = "mock-review-3", businessId = "fake-shiraz-palace", authorUid = "mock-user-marc",
            authorName = "Marc L.", rating = 3,
            comment = "Food was good but parking is tough. Call ahead for takeout.",
            createdAtMillis = daysAgo(21),
        ),
        Review(
            id = "mock-review-4", businessId = ownedBusiness.id, authorUid = "mock-user-neda",
            authorName = "Neda T.", rating = 5,
            comment = "Fresh sangak every morning. The owner remembers everyone's order!",
            createdAtMillis = daysAgo(2),
        ),
        Review(
            id = "mock-review-5", businessId = realtorBusiness.id, authorUid = "mock-user-neda",
            authorName = "Neda T.", rating = 5,
            comment = "Sold our condo over asking in 9 days and walked us through every form in Farsi. Cannot recommend enough.",
            tags = listOf("Professional", "Great communication"),
            ownerReply = "Merci Neda jan — enjoy the new place, and the plants are yours to keep!",
            createdAtMillis = daysAgo(5),
        ),
        Review(
            id = "mock-review-6", businessId = realtorBusiness.id, authorUid = "mock-user-marc",
            authorName = "Marc L.", rating = 4,
            comment = "Knowledgeable about the newcomer mortgage programs. Booking a viewing took a couple of tries.",
            createdAtMillis = daysAgo(14),
        ),
    )

    // ── Chat ────────────────────────────────────────────────────────────────
    val conversations: List<Conversation> = listOf(
        Conversation(
            id = "${MOCK_UID}_fake-shiraz-palace",
            businessId = "fake-shiraz-palace",
            businessName = "Shiraz Palace",
            participantUids = listOf(MOCK_UID, "mock-owner-shiraz"),
            lastMessage = "We have a table for 4 at 7:30 tonight — shall I hold it?",
            lastMessageAtMillis = minutesAgo(12),
            unreadCount = 2,
        ),
        // Customer lead for the owned realty — appears in the inbox from the owner's side.
        Conversation(
            id = "mock-user-neda_mock-maple-homes",
            businessId = realtorBusiness.id,
            businessName = realtorBusiness.name,
            participantUids = listOf("mock-user-neda", MOCK_UID),
            lastMessage = "Is the North York condo still available? We could view it this weekend.",
            lastMessageAtMillis = hoursAgo(1),
            unreadCount = 1,
        ),
        Conversation(
            id = "item_mock-item-rug_mock-user-neda",
            businessId = "",
            businessName = "Persian wool rug 2×3m",
            itemId = "mock-item-rug",
            participantUids = listOf(MOCK_UID, "mock-user-neda"),
            lastMessage = "Is the rug still available? Could you do $380?",
            lastMessageAtMillis = hoursAgo(5),
            unreadCount = 1,
        ),
    )

    val messages: Map<String, List<Message>> = mapOf(
        "${MOCK_UID}_fake-shiraz-palace" to listOf(
            Message("mock-msg-1", MOCK_UID, "Salam! Do you take reservations for tonight?", hoursAgo(2), isMine = true),
            Message("mock-msg-2", "mock-owner-shiraz", "Salam! Of course — how many people?", minutesAgo(90)),
            Message("mock-msg-3", MOCK_UID, "4 people, around 7:30 if possible.", minutesAgo(80), isMine = true),
            Message("mock-msg-4", "mock-owner-shiraz", "We have a table for 4 at 7:30 tonight — shall I hold it?", minutesAgo(12)),
        ),
        "mock-user-neda_mock-maple-homes" to listOf(
            Message("mock-msg-8", "mock-user-neda", "Hi! I saw the 2-bed condo listing on your page.", hoursAgo(2)),
            Message("mock-msg-9", MOCK_UID, "Great choice — it's a bright corner unit. Want the floor plan?", minutesAgo(90), isMine = true),
            Message("mock-msg-10", "mock-user-neda", "Is the North York condo still available? We could view it this weekend.", hoursAgo(1)),
        ),
        "item_mock-item-rug_mock-user-neda" to listOf(
            Message("mock-msg-5", "mock-user-neda", "Hi! Beautiful rug. Is it from Tabriz?", hoursAgo(6)),
            Message("mock-msg-6", MOCK_UID, "Yes, hand-knotted Tabriz — barely used.", hoursAgo(6), isMine = true),
            Message("mock-msg-7", "mock-user-neda", "Is the rug still available? Could you do $380?", hoursAgo(5)),
        ),
    )

    // ── Notifications — one of every type the bell can show ─────────────────
    val notifications: List<AppNotification> = listOf(
        // The daily Hidden Gem. Seeded so mock mode can exercise the gem tap route — the surface
        // that shipped with a dead tap because nothing here covered it.
        AppNotification(
            id = "mock-notif-gem", type = NotificationType.Gem,
            gemKind = "joke", gemId = "joke-taarof-samovar",
            title = "Joke of the day",
            preview = "An Iranian refuses tea three times out of politeness. On the fourth offer, " +
                "he accepts — and takes the whole samovar.",
            read = false, createdAtMillis = minutesAgo(5),
        ),
        AppNotification(
            id = "mock-notif-message", type = NotificationType.Message,
            conversationId = conversations[0].id, businessId = "fake-shiraz-palace",
            title = "Shiraz Palace",
            preview = "We have a table for 4 at 7:30 tonight — shall I hold it?",
            photoUrl = "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=200&q=60",
            read = false, createdAtMillis = minutesAgo(12),
        ),
        AppNotification(
            id = "mock-notif-review-reply", type = NotificationType.ReviewReply,
            businessId = "fake-shiraz-palace", reviewId = "mock-review-2",
            title = "Shiraz Palace",
            preview = "Thank you for the kind words! Fridays are busy — next time try Thursday.",
            read = false, createdAtMillis = hoursAgo(3),
        ),
        AppNotification(
            id = "mock-notif-comment", type = NotificationType.Comment,
            postId = "mock-post-question", title = "Neda T.",
            preview = "Caspian Dental on Lonsdale is great with kids — ask for Dr. Rahimi.",
            read = false, createdAtMillis = hoursAgo(8),
        ),
        AppNotification(
            id = "mock-notif-order", type = NotificationType.Order,
            orderId = "mock-order-confirmed", orderStatus = "confirmed",
            title = ownedBusiness.name,
            read = false, createdAtMillis = hoursAgo(26),
        ),
        AppNotification(
            id = "mock-notif-event", type = NotificationType.Event,
            eventId = "mock-event-nowruz", title = "Nowruz Bazaar & Concert",
            preview = "New event in Toronto",
            read = false, createdAtMillis = daysAgo(2),
        ),
        AppNotification(
            id = "mock-notif-campaign", type = NotificationType.Campaign,
            link = "https://mahaleh.ca/event/mock-event-nowruz",
            title = "Nowruz is coming!",
            preview = "The Nowruz Bazaar & Concert is back — free tickets are live now.",
            read = false, createdAtMillis = daysAgo(1),
        ),
        AppNotification(
            id = "mock-notif-channel", type = NotificationType.Channel,
            businessId = "fake-shiraz-palace", title = "Shiraz Palace",
            preview = "New post: our Yalda-night tasting menu is live — reserve early!",
            read = false, createdAtMillis = hoursAgo(20),
        ),
        // Legacy doc written before display snapshots shipped — exercises the generic fallback row.
        AppNotification(
            id = "mock-notif-legacy", type = NotificationType.Message,
            conversationId = conversations[0].id,
            read = true, createdAtMillis = daysAgo(6),
        ),
    )

    // ── Commerce: products + orders (every status) ──────────────────────────
    val products: Map<String, List<Product>> = mapOf(
        ownedBusiness.id to listOf(
            Product(
                id = "mock-prod-koobideh", businessId = ownedBusiness.id,
                name = LocalizedText(en = "Koobideh plate", fa = "چلوکباب کوبیده"),
                description = LocalizedText(en = "Two skewers, saffron rice, grilled tomato."),
                priceCents = 1895, promoted = true,
            ),
            Product(
                id = "mock-prod-sangak", businessId = ownedBusiness.id,
                name = LocalizedText(en = "Fresh sangak", fa = "نان سنگک"),
                priceCents = 450,
            ),
            Product(
                id = "mock-prod-faloodeh", businessId = ownedBusiness.id,
                name = LocalizedText(en = "Faloodeh (seasonal)", fa = "فالوده"),
                priceCents = 700, available = false,
            ),
        ),
        realtorBusiness.id to listOf(
            // The PROMOTED listing — renders in the Business Detail Promotions rail.
            Product(
                id = "mock-prod-condo", businessId = realtorBusiness.id,
                name = LocalizedText(en = "2-bed condo — North York", fa = "آپارتمان دوخوابه — نورث یورک"),
                description = LocalizedText(en = "Bright corner unit near Mel Lastman Square. Parking + locker included."),
                priceCents = 74_900_000, promoted = true,
                imageUrl = "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=1000&q=70",
                property = PropertyDetails(bedrooms = 2, bathrooms = 2.0, areaSqFt = 890, address = "18 Hollywood Ave, North York"),
            ),
            Product(
                id = "mock-prod-house", businessId = realtorBusiness.id,
                name = LocalizedText(en = "Detached 4-bed — Richmond Hill"),
                description = LocalizedText(en = "Renovated kitchen, finished basement, steps to Bayview Secondary."),
                priceCents = 168_800_000,
                imageUrl = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1000&q=70",
                property = PropertyDetails(bedrooms = 4, bathrooms = 3.5, areaSqFt = 2450, address = "45 Bathurst Glen Dr"),
            ),
            // Monthly rental — exercises the rent-style price on a property listing.
            Product(
                id = "mock-prod-rental", businessId = realtorBusiness.id,
                name = LocalizedText(en = "2-bed rental — Yonge & Eglinton (monthly)"),
                priceCents = 289_500,
                property = PropertyDetails(bedrooms = 2, bathrooms = 1.0, areaSqFt = 780, address = "2191 Yonge St"),
            ),
            // Non-property service — proves plain products and property listings coexist.
            Product(
                id = "mock-prod-staging", businessId = realtorBusiness.id,
                name = LocalizedText(en = "Home staging consultation"),
                description = LocalizedText(en = "90-minute walkthrough with a written staging plan."),
                priceCents = 9_900,
            ),
            // Unavailable listing — exercises the sold/unavailable state.
            Product(
                id = "mock-prod-sold-condo", businessId = realtorBusiness.id,
                name = LocalizedText(en = "SOLD — 1-bed loft, Liberty Village"),
                priceCents = 61_500_000, available = false,
                property = PropertyDetails(bedrooms = 1, bathrooms = 1.0, areaSqFt = 610),
            ),
        ),
    )

    private fun order(id: String, status: OrderStatus, payment: PaymentStatus, ageHours: Int, note: String = "") = Order(
        id = id,
        businessId = ownedBusiness.id,
        businessName = ownedBusiness.name,
        items = listOf(
            OrderItem("mock-prod-koobideh", "Koobideh plate", 2, 1895),
            OrderItem("mock-prod-sangak", "Fresh sangak", 1, 450),
        ),
        subtotalCents = 4240,
        totalCents = 4240,
        status = status,
        paymentStatus = payment,
        note = note,
        createdAtMillis = hoursAgo(ageHours),
    )

    /** One order in every status, newest first — order history + confirmation + dashboard cases. */
    val orders: List<Order> = listOf(
        order("mock-order-pending", OrderStatus.Pending, PaymentStatus.Unpaid, 2, note = "Extra somagh please"),
        order("mock-order-confirmed", OrderStatus.Confirmed, PaymentStatus.Unpaid, 26),
        order("mock-order-fulfilled", OrderStatus.Fulfilled, PaymentStatus.Paid, 24 * 4),
        order("mock-order-cancelled", OrderStatus.Cancelled, PaymentStatus.Refunded, 24 * 9),
    )

    // ── Marketplace ─────────────────────────────────────────────────────────
    val marketplaceItems: List<MarketplaceItem> = listOf(
        MarketplaceItem(
            id = "mock-item-rug", sellerUid = MOCK_UID, sellerName = MOCK_NAME,
            title = "Persian wool rug 2×3m", description = "Hand-knotted Tabriz rug, deep red. Moving sale.",
            priceCents = 42_000, condition = ItemCondition.LikeNew, metroId = "gta", city = "Toronto",
            photoUrls = listOf("https://images.unsplash.com/photo-1600166898405-da9535204843?auto=format&fit=crop&w=1000&q=70"),
            createdAtMillis = daysAgo(4),
        ),
        MarketplaceItem(
            id = "mock-item-samovar", sellerUid = "mock-user-neda", sellerName = "Neda T.",
            title = "Vintage brass samovar", description = "Works beautifully, small dent on the base.",
            priceCents = 9_500, condition = ItemCondition.Used, metroId = "gta", city = "North York",
            photoUrls = listOf("https://images.unsplash.com/photo-1544787219-7f47ccb76574?auto=format&fit=crop&w=1000&q=70"),
            createdAtMillis = daysAgo(1),
        ),
        MarketplaceItem(
            id = "mock-item-books", sellerUid = "mock-user-marc", sellerName = "Marc L.",
            title = "Farsi children's books (12)", description = "Free to a good home — great condition.",
            isFree = true, condition = ItemCondition.Used, metroId = "mtl", city = "Montréal",
            createdAtMillis = hoursAgo(9),
        ),
        MarketplaceItem(
            id = "mock-item-bike", sellerUid = "mock-user-neda", sellerName = "Neda T.",
            title = "City bike, medium frame", description = "Sold — pickup completed.",
            priceCents = 18_000, condition = ItemCondition.Used, metroId = "gta", city = "Toronto",
            status = ItemStatus.Sold, createdAtMillis = daysAgo(12),
        ),
    )

    // ── Events + tickets ────────────────────────────────────────────────────
    val events: List<Event> = listOf(
        Event(
            id = "mock-event-nowruz", organizerUid = "mock-user-neda",
            title = LocalizedText(en = "Nowruz Bazaar & Concert", fa = "بازارچه و کنسرت نوروزی"),
            description = LocalizedText(en = "Haft-seen market, live santur, and free tea. Family friendly."),
            coverUrl = "https://images.unsplash.com/photo-1514525253161-7a46d19cd819?auto=format&fit=crop&w=1000&q=70",
            startsAtMillis = daysAheadAt(9), endsAtMillis = daysAheadAt(9) + 5 * 3_600_000L,
            venue = EventVenue(name = "Mel Lastman Square", address = "5100 Yonge St", city = "Toronto", province = "ON"),
            category = EventCategory.Culture, metroId = "gta", isFree = true, featured = true,
        ),
        Event(
            id = "mock-event-concert", organizerUid = "mock-user-marc",
            title = LocalizedText(en = "Persian Classical Night"),
            description = LocalizedText(en = "An evening of tar and setar. Tickets on the organizer's site."),
            startsAtMillis = daysAheadAt(20),
            venue = EventVenue(name = "Place des Arts", city = "Montréal", province = "QC"),
            category = EventCategory.Music, metroId = "mtl",
            ticketUrl = "https://example.com/tickets",
        ),
        Event(
            id = "mock-event-yalda", organizerUid = "mock-user-neda",
            title = LocalizedText(en = "Yalda Family Picnic", fa = "پیک‌نیک شب یلدا"),
            startsAtMillis = daysAheadAt(35, hour = 17),
            venue = EventVenue(name = "Stanley Park", city = "Vancouver", province = "BC"),
            category = EventCategory.Family, metroId = "yvr", isFree = true,
        ),
        // Two more in the GTA so the Events list looks like a real week in the city rather than a
        // single card (the other two above sit in other metros, which the city filter hides).
        Event(
            id = "mock-event-poetry", organizerUid = "mock-user-marc",
            title = LocalizedText(en = "Shab-e She'r — Poetry Night", fa = "شب شعر"),
            description = LocalizedText(en = "Open mic for Hafez, Rumi, and your own. Tea and shirini provided."),
            startsAtMillis = daysAheadAt(4), endsAtMillis = daysAheadAt(4) + 3 * 3_600_000L,
            venue = EventVenue(name = "North York Central Library", address = "5120 Yonge St", city = "Toronto", province = "ON"),
            category = EventCategory.Culture, metroId = "gta", isFree = true,
        ),
        Event(
            id = "mock-event-cooking", organizerUid = "mock-user-neda",
            title = LocalizedText(en = "Ghormeh Sabzi Workshop", fa = "کارگاه قرمه‌سبزی"),
            description = LocalizedText(en = "Cook the classic from scratch, then eat together. Ingredients included."),
            startsAtMillis = daysAheadAt(12, hour = 11), endsAtMillis = daysAheadAt(12, hour = 11) + 4 * 3_600_000L,
            venue = EventVenue(name = "Harbourfront Centre", address = "235 Queens Quay W", city = "Toronto", province = "ON"),
            category = EventCategory.Culture, metroId = "gta",
        ),
    )

    /** Ticket types for the free event: one open, one sold out (capacity UI). */
    val ticketTypes: Map<String, List<TicketType>> = mapOf(
        "mock-event-nowruz" to listOf(
            TicketType(id = "mock-tt-ga", name = "General admission", quantityTotal = 200, quantitySold = 137),
            TicketType(id = "mock-tt-vip", name = "Front row", quantityTotal = 20, quantitySold = 20),
        ),
    )

    val tickets: List<Ticket> = listOf(
        Ticket(
            id = "mock-ticket-valid", eventId = "mock-event-nowruz", ticketTypeId = "mock-tt-ga",
            qty = 2, qrPayload = "MOCK-QR-VALID-0001", status = TicketStatus.Valid,
            eventTitle = LocalizedText(en = "Nowruz Bazaar & Concert", fa = "بازارچه و کنسرت نوروزی"),
            startsAtMillis = daysAheadAt(9), venueName = "Mel Lastman Square",
            purchasedAtMillis = daysAgo(2),
        ),
        Ticket(
            id = "mock-ticket-used", eventId = "mock-event-concert", ticketTypeId = "mock-tt-past",
            qty = 1, qrPayload = "MOCK-QR-USED-0002", status = TicketStatus.Used,
            eventTitle = LocalizedText(en = "Shab-e She'r — Poetry Night"),
            startsAtMillis = daysAgo(30), venueName = "North York Central Library",
            purchasedAtMillis = daysAgo(33),
        ),
    )

    // ── News ────────────────────────────────────────────────────────────────
    val news: List<NewsArticle> = listOf(
        NewsArticle(
            id = "mock-news-festival",
            title = LocalizedText(en = "Tirgan Festival returns this August", fa = "جشنواره تیرگان برمی‌گردد"),
            summary = LocalizedText(en = "Three days of music, food, and art on the Harbourfront."),
            body = LocalizedText(en = "The largest Iranian festival in North America returns to Toronto's Harbourfront Centre this August with 200+ artists."),
            imageUrl = "https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?auto=format&fit=crop&w=1000&q=70",
            metroId = "gta", publishedAtMillis = daysAgo(1),
        ),
        NewsArticle(
            id = "mock-news-welcome",
            title = LocalizedText(en = "Welcome to Mahaleh!", fa = "به محله خوش آمدید!"),
            summary = LocalizedText(en = "Discover Iranian-owned businesses across Canada."),
            body = LocalizedText(en = "Mahaleh is your neighbourhood: find trusted businesses, events, and community across Canada."),
            publishedAtMillis = daysAgo(5),
        ),
    )

    // ── Community feed ──────────────────────────────────────────────────────
    val posts: List<Post> = listOf(
        Post(
            id = "mock-post-question", authorUid = MOCK_UID, authorName = MOCK_NAME,
            type = PostType.Question, metroId = "gta",
            text = "Any recommendations for a dentist who's good with anxious kids? Preferably North Shore.",
            commentCount = 2, likeCount = 3, createdAtMillis = hoursAgo(9),
        ),
        Post(
            id = "mock-post-photo", authorUid = "mock-user-neda", authorName = "Neda T.",
            type = PostType.General, metroId = "gta",
            text = "First snow at the bazaar pop-up — the saffron hot chocolate was worth the line ❄️",
            photoUrl = "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1000&q=70",
            commentCount = 1, likeCount = 12, createdAtMillis = daysAgo(1),
        ),
        Post(
            id = "mock-post-reco", authorUid = "mock-user-marc", authorName = "Marc L.",
            type = PostType.Recommendation, metroId = "mtl",
            text = "If you need an immigration lawyer who answers the phone, Azadi Law is the one. They handled my parents' PR file flawlessly.",
            likeCount = 7, createdAtMillis = daysAgo(2),
        ),
    )

    val comments: Map<String, List<Comment>> = mapOf(
        "mock-post-question" to listOf(
            Comment(
                id = "mock-comment-1", authorUid = "mock-user-neda", authorName = "Neda T.",
                text = "Caspian Dental on Lonsdale is great with kids — ask for Dr. Rahimi.",
                recommendedBusinessId = "fake-caspian-dental", recommendedBusinessName = "Caspian Dental",
                createdAtMillis = hoursAgo(8),
            ),
            Comment(
                id = "mock-comment-2", authorUid = "mock-user-marc", authorName = "Marc L.",
                text = "Seconding Caspian — they have a TV on the ceiling for the kids.",
                createdAtMillis = hoursAgo(6),
            ),
        ),
        "mock-post-photo" to listOf(
            Comment(
                id = "mock-comment-3", authorUid = MOCK_UID, authorName = MOCK_NAME,
                text = "Okay now I need to find this pop-up 😍", createdAtMillis = hoursAgo(20), isMine = true,
            ),
        ),
    )

    // ── Admin queues ────────────────────────────────────────────────────────
    val pendingEvents: List<Event> = listOf(
        events[2].copy(id = "mock-event-pending", title = LocalizedText(en = "Charshanbe Suri Bonfire"), status = EventStatus.Pending),
    )

    val openReports: List<Report> = listOf(
        Report(
            id = "mock-report-1", targetType = "post", targetId = "mock-post-photo",
            reporterUid = "mock-user-marc", reason = "Spam / duplicate posting", status = ReportStatus.Open,
        ),
    )

    val claims: List<BusinessClaim> = listOf(
        BusinessClaim(
            id = "mock-claim-1", businessId = "fake-pars-market", businessName = "Pars Market",
            claimantUid = "mock-user-neda", claimantName = "Neda T.", role = ClaimRole.Owner,
            contactEmail = "neda@example.com", contactPhone = "+14165550142",
            message = "I opened this store in 2019 — business licence and utility bill on file.",
            createdAtMillis = daysAgo(1),
        ),
    )

    val userAccounts: List<UserAccount> = listOf(
        UserAccount(uid = MOCK_UID, displayName = MOCK_NAME, role = UserAccount.ROLE_ADMIN, metroId = "gta", preferredLanguage = "en"),
        UserAccount(uid = "mock-user-neda", displayName = "Neda T.", metroId = "gta", preferredLanguage = "fa"),
        UserAccount(uid = "mock-user-marc", displayName = "Marc L.", metroId = "mtl", preferredLanguage = "fr"),
        UserAccount(uid = "mock-user-blocked", displayName = "Spam Account", metroId = "gta", preferredLanguage = "en"),
    )

    // ── Dashboard analytics ─────────────────────────────────────────────────
    val listingCounters: Map<String, ListingCounters> = mapOf(
        ownedBusiness.id to ListingCounters(views = 412, calls = 37, directions = 58, chats = 21, shares = 9),
        realtorBusiness.id to ListingCounters(views = 963, calls = 54, directions = 12, chats = 88, shares = 31),
    )

    val dailyCounters: Map<String, List<DailyListingCounters>> = mapOf(
        ownedBusiness.id to (6 downTo 0).map { d ->
            DailyListingCounters(
                day = dayKey(daysAgo(d)),
                counters = ListingCounters(
                    views = 30L + (6 - d) * 9, calls = 2L + (6 - d), directions = 4L + (6 - d) * 2,
                    chats = 1L + (6 - d), shares = (6 - d) / 2L,
                ),
            )
        },
        realtorBusiness.id to (6 downTo 0).map { d ->
            DailyListingCounters(
                day = dayKey(daysAgo(d)),
                counters = ListingCounters(
                    views = 90L + (6 - d) * 14, calls = 4L + (6 - d), directions = 1L + (6 - d) / 2,
                    chats = 8L + (6 - d) * 2, shares = 2L + (6 - d),
                ),
            )
        },
    )

    /** yyyymmdd bucket key for a timestamp (UTC day — matches the Functions counters). */
    private fun dayKey(millis: Long): String {
        val date = Instant.fromEpochMilliseconds(millis).toLocalDateTime(TimeZone.UTC).date
        val mm = date.month.number.toString().padStart(2, '0')
        val dd = date.day.toString().padStart(2, '0')
        return "${date.year}$mm$dd"
    }

    // ── Find your people + the people map ───────────────────────────────────
    /**
     * Discoverable members for the Find-your-people list, the Home people rail and the people MAP.
     * Deliberately **photo-free** — the initials avatar renders instead, so no stranger's face ever
     * appears in a demo or a store screenshot. Coordinates are already snapped to the check-in grids
     * (0.1° city / 0.02° neighbourhood) and each carries a live check-in expiry, because the map only
     * draws members whose manual check-in has not run out (App Store 5.1.2).
     */
    val people: List<PublicProfile> = listOf(
        publicProfile(
            uid = "mock-user-neda", name = "Neda T.", city = "North York", lat = 43.76, lng = -79.40,
            precision = MapPrecision.Neighbourhood, bio = "New in Toronto, learning to bake barbari. Always up for a chai.",
            intents = listOf("friends", "chatting"), occupation = "Pharmacist", hometown = "Tehran",
            languages = listOf("fa", "en"), interests = listOf("Baking", "Hiking", "Film"),
        ),
        publicProfile(
            uid = "mock-user-marc", name = "Marc L.", city = "Toronto", lat = 43.70, lng = -79.40,
            precision = MapPrecision.City, bio = "Grad student at UofT. Looking for a Farsi conversation partner.",
            intents = listOf("networking", "meet"), occupation = "Grad student", hometown = "Montréal",
            languages = listOf("fr", "en", "fa"), interests = listOf("Board games", "Cycling"),
        ),
        publicProfile(
            uid = "mock-user-sara", name = "Sara K.", city = "Richmond Hill", lat = 43.88, lng = -79.44,
            precision = MapPrecision.Neighbourhood, bio = "Mum of two, organising a Nowruz picnic this year.",
            intents = listOf("friends"), occupation = "Nurse", hometown = "Shiraz",
            languages = listOf("fa", "en"), interests = listOf("Gardening", "Poetry"),
        ),
        publicProfile(
            uid = "mock-user-arash", name = "Arash M.", city = "Toronto", lat = 43.64, lng = -79.38,
            precision = MapPrecision.Neighbourhood, bio = "Software engineer, weekend santur player.",
            intents = listOf("networking", "chatting"), occupation = "Software engineer", hometown = "Esfahan",
            languages = listOf("fa", "en"), interests = listOf("Music", "Chess", "Coffee"),
        ),
        publicProfile(
            uid = "mock-user-mina", name = "Mina R.", city = "Vaughan", lat = 43.82, lng = -79.52,
            precision = MapPrecision.City, bio = "Just moved from Vancouver — recommend me a good sangak bakery?",
            intents = listOf("meet", "friends"), occupation = "Architect", hometown = "Tabriz",
            languages = listOf("fa", "en"), interests = listOf("Design", "Running"),
        ),
    )

    private fun publicProfile(
        uid: String,
        name: String,
        city: String,
        lat: Double,
        lng: Double,
        precision: MapPrecision,
        bio: String,
        intents: List<String>,
        occupation: String,
        hometown: String,
        languages: List<String>,
        interests: List<String>,
    ) = PublicProfile(
        uid = uid,
        displayName = name,
        city = city,
        bio = bio,
        intents = intents,
        discoverable = true,
        mapVisible = true,
        approxLat = lat,
        approxLng = lng,
        mapPrecision = precision,
        // Staggered so the "time left" line differs per member, as it would in a real neighbourhood.
        mapCheckInExpiresAtMillis = now + PEOPLE_MAP_CHECK_IN_MILLIS - (uid.length % 4) * 25 * 60_000L,
        occupation = occupation,
        hometown = hometown,
        languages = languages,
        interests = interests,
    )

    // ── Capture-safe (store screenshots) ────────────────────────────────────
    /**
     * Copies with every third-party stock-photo URL dropped, used when
     * [MockDataController.screenshotMode] is on. The app then renders its OWN category emblem +
     * gradient artwork in place of each photo, so a store screenshot contains only art we own and
     * invented text — no production data, no member's photo, no stranger's face.
     */
    fun photoFreeBusinesses(): List<Business> = businesses.map(::photoFree)

    fun photoFreeOwned(): List<Business> = listOf(ownedBusiness, realtorBusiness).map(::photoFree)

    private fun photoFree(business: Business): Business = business.copy(
        logoUrl = null,
        coverUrl = null,
        photos = emptyList(),
        mediaStatus = MediaStatus.CategoryPlaceholderPending,
    )

    fun photoFreeEvents(): List<Event> = events.map { it.copy(coverUrl = null) }

    fun photoFreeNews(): List<NewsArticle> = news.map { it.copy(imageUrl = null) }

    fun photoFreePosts(): List<Post> = posts.map { it.copy(photoUrl = null) }

    fun photoFreeMarketplaceItems(): List<MarketplaceItem> =
        marketplaceItems.map { it.copy(photoUrls = emptyList()) }

    fun photoFreeProducts(): Map<String, List<Product>> =
        products.mapValues { (_, items) -> items.map { it.copy(imageUrl = null) } }

    // ── Profile + trust/safety ──────────────────────────────────────────────
    val profile = UserProfile(
        displayName = MOCK_NAME,
        bio = "QA persona — everything on this account is mock data.",
        createdAtMillis = daysAgo(180),
        pushEnabled = true,
    )

    val blockedUsers: List<BlockedUser> = listOf(
        BlockedUser(uid = "mock-user-blocked", name = "Spam Account"),
    )
}
